<template>

  <!-- ══════════════════════════════════════════════
       isFirstTimeLogin == true  →  full-page overlay
  ══════════════════════════════════════════════ -->
  <div v-if="isFirstTimeLogin" class="change-pass-page">
    <div class="cp-layout">

      <!-- LEFT PANEL -->
      <div class="left-panel">
        <div class="lp-top">
          <div class="lp-logo">
            <div class="lp-logo-box">
              <img src="../../assets/img/logo_icon.png" alt="InternLink" />
            </div>
            <span class="lp-logo-text">InternLink</span>
          </div>
        </div>

        <div class="lp-hero">
          <div class="lp-tagline">
            Secure Your
            <span class="lp-tagline-green">Account Access.</span>
          </div>
          <div class="lp-caption">
            You're almost in. Set a strong password to protect your account
            before accessing any features of the platform.
          </div>
        </div>

        <div class="lp-bottom">
          <div class="lp-features">
            <div class="lp-feature-item">
              <v-icon color="#5ee9b5" size="20">mdi-lock-check-outline</v-icon>
              <span>Minimum 8 characters required</span>
            </div>
            <div class="lp-feature-item">
              <v-icon color="#5ee9b5" size="20">mdi-shield-lock-outline</v-icon>
              <span>Mix letters, numbers &amp; symbols</span>
            </div>
            <div class="lp-feature-item">
              <v-icon color="#5ee9b5" size="20">mdi-eye-off-outline</v-icon>
              <span>Never share your password</span>
            </div>
          </div>
          <hr class="lp-divider" />
          <div class="lp-footer">
            <span>© 2026 InternLink Systems. All rights reserved.</span>
          </div>
        </div>
      </div>

      <!-- RIGHT PANEL -->
      <div class="right-panel">
        <div class="cp-card rounded-xl">

          <div class="d-flex justify-center mb-5">
            <div class="card-logo-box rounded-xl">
              <img src="../../assets/img/internLink_logo.png" alt="InternLink" width="44" />
            </div>
          </div>

          <div class="lc-title mb-1">Set your new password</div>
          <div class="lc-subtitle mb-6">
            Welcome! Please update your credentials to continue.
          </div>

          <v-form ref="ChangePassRef">

            <div class="field-group mb-4">
              <div class="field-label">Current Password</div>
              <v-text-field
                v-model="cur_pass"
                :rules="[formRules.required]"
                placeholder="••••••••"
                prepend-inner-icon="mdi-lock-outline"
                :type="show_cur_pass ? 'text' : 'password'"
                :append-inner-icon="show_cur_pass ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="show_cur_pass = !show_cur_pass"
                :color="$vuetify.theme.themes.light.inputColor"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <div class="field-group mb-4">
              <div class="field-label">New Password</div>
              <v-text-field
                v-model="new_pass"
                :rules="[formRules.required, formRules.password]"
                placeholder="••••••••"
                prepend-inner-icon="mdi-lock-reset"
                :type="show_new_pass ? 'text' : 'password'"
                :append-inner-icon="show_new_pass ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="show_new_pass = !show_new_pass"
                :color="$vuetify.theme.themes.light.inputColor"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <div class="field-group mb-6">
              <div class="field-label">Confirm New Password</div>
              <v-text-field
                v-model="confirm_pass"
                :rules="[
                  formRules.required,
                  formRules.password,
                  (v) => v === this.new_pass || 'Passwords do not match',
                ]"
                placeholder="••••••••"
                prepend-inner-icon="mdi-lock-check-outline"
                :type="show_confirm_pass ? 'text' : 'password'"
                :append-inner-icon="show_confirm_pass ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="show_confirm_pass = !show_confirm_pass"
                @keyup.enter="submit()"
                :color="$vuetify.theme.themes.light.inputColor"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <v-btn
              rounded="pill"
              color="#10b981"
              :loading="btnLoading"
              :disabled="btnLoading"
              block
              size="large"
              class="text-white submit-btn"
              @click="submit()"
              append-icon="mdi-arrow-right"
            >
              Update Password
            </v-btn>

          </v-form>
        </div>
      </div>
    </div>

    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>

  <!-- ══════════════════════════════════════════════
       isFirstTimeLogin == false  →  settings content
       (MainLayout already provides sidebar + appbar)
  ══════════════════════════════════════════════ -->
  <div v-else class="settings-page">

    <div class="settings-header">
      <h1 class="settings-title">Settings</h1>
      <p class="settings-subtitle">Manage your account settings and preferences</p>
    </div>

    <!-- Tabs -->
    <div class="settings-tab-bar">
      <button
        class="settings-tab"
        :class="{ 'settings-tab--active': activeTab === 'password' }"
        @click="activeTab = 'password'"
      >
        <v-icon size="15">mdi-lock-outline</v-icon>
        Password &amp; Security
      </button>
      <button
        v-if="isCoordinator"
        class="settings-tab"
        :class="{ 'settings-tab--active': activeTab === 'roles' }"
        @click="activeTab = 'roles'"
      >
        <v-icon size="15">mdi-account-switch-outline</v-icon>
        Switch Role
      </button>
    </div>

    <div class="settings-content">

      <!-- ── Switch Role tab ── -->
      <template v-if="activeTab === 'roles' && isCoordinator">

        <div v-if="rolesLoading" class="d-flex justify-center py-10">
          <v-progress-circular indeterminate color="#10b981" size="36" />
        </div>

        <template v-else-if="roles.length">
          <v-row>
            <v-col
              v-for="(role, idx) in roles"
              :key="role.id"
              cols="12" sm="6" md="4" lg="3"
            >
              <div
                class="role-card rounded-xl pa-6"
                :class="activeRole === role.id ? 'role-card--active' : 'role-card--idle'"
                @click="selectRole(role.id)"
              >
                <div
                  class="role-icon-box rounded-lg mb-5"
                  :style="{ backgroundColor: palette(idx).box }"
                >
                  <v-icon size="26" :color="palette(idx).icon">{{ palette(idx).mdi }}</v-icon>
                </div>
                <div class="role-name mb-2">{{ role.description }}</div>
                <v-chip
                  v-if="activeRole === role.id"
                  color="#10b981"
                  size="small"
                  variant="flat"
                  prepend-icon="mdi-check-circle-outline"
                  class="text-white"
                  style="font-size:11px;font-weight:600;"
                >Active</v-chip>
                <span v-else class="role-hint">Click to activate</span>
                <div v-if="activeRole === role.id" class="active-bar" />
              </div>
            </v-col>
          </v-row>
        </template>

        <div v-else class="d-flex flex-column align-center py-10">
          <v-icon size="64" color="#cbd5e1">mdi-account-multiple-outline</v-icon>
          <div class="mt-4" style="font-size:18px;font-weight:600;color:#475569;">No roles assigned</div>
          <div class="mt-1" style="font-size:14px;color:#94a3b8;">Contact your administrator to get roles assigned.</div>
        </div>

      </template>

      <!-- ── Password & Security tab ── -->
      <template v-if="activeTab === 'password'">
      <!-- Change Password card -->
      <div class="cp-settings-card">
          <div class="cp-settings-header">
            <div class="cp-settings-icon-wrap">
              <v-icon color="#10b981" size="20">mdi-lock-outline</v-icon>
            </div>
            <div>
              <div class="cp-settings-title">Change Password</div>
              <div class="cp-settings-desc">Update your password to keep your account secure</div>
            </div>
          </div>

          <v-form ref="ChangePassRef">

            <div class="field-group mb-4">
              <div class="field-label">Current Password</div>
              <v-text-field
                v-model="cur_pass"
                :rules="[formRules.required]"
                placeholder="Enter current password"
                :type="show_cur_pass ? 'text' : 'password'"
                :append-inner-icon="show_cur_pass ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="show_cur_pass = !show_cur_pass"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <div class="field-group mb-4">
              <div class="field-label">New Password</div>
              <v-text-field
                v-model="new_pass"
                :rules="[formRules.required, formRules.password]"
                placeholder="Enter new password"
                :type="show_new_pass ? 'text' : 'password'"
                :append-inner-icon="show_new_pass ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="show_new_pass = !show_new_pass"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <div class="field-group mb-4">
              <div class="field-label">Confirm New Password</div>
              <v-text-field
                v-model="confirm_pass"
                :rules="[
                  formRules.required,
                  formRules.password,
                  (v) => v === this.new_pass || 'Passwords do not match',
                ]"
                placeholder="Confirm new password"
                :type="show_confirm_pass ? 'text' : 'password'"
                :append-inner-icon="show_confirm_pass ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="show_confirm_pass = !show_confirm_pass"
                @keyup.enter="submit()"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                rounded="lg"
              />
            </div>

            <div class="pass-requirements mb-5">
              <p class="pass-req-title">Password requirements:</p>
              <ul class="pass-req-list">
                <li>At least 8 characters long</li>
                <li>Contains uppercase letter (A-Z)</li>
                <li>Contains lowercase letter (a-z)</li>
                <li>Contains number (0-9)</li>
                <li>Contains a special character (!@#$...)</li>
              </ul>
            </div>

            <v-btn
              rounded="lg"
              color="#10b981"
              :loading="btnLoading"
              :disabled="btnLoading"
              block
              size="large"
              class="text-white submit-btn"
              @click="submit()"
              prepend-icon="mdi-content-save-outline"
            >
              Change Password
            </v-btn>

          </v-form>
      </div>
      </template>

    </div>

    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>

</template>

<script>
const ROLE_PALETTE = [
  { box: "#ecfdf5", icon: "#059669", mdi: "mdi-account-tie-outline" },
  { box: "#eff6ff", icon: "#2563eb", mdi: "mdi-shield-account-outline" },
  { box: "#fef3c7", icon: "#d97706", mdi: "mdi-account-star-outline" },
  { box: "#f5f3ff", icon: "#7c3aed", mdi: "mdi-account-badge-outline" },
  { box: "#fff1f2", icon: "#e11d48", mdi: "mdi-account-supervisor-outline" },
];

export default {
  created() {
    if (this.$store.state.expiryDate < Date.now()) {
      this.$store.dispatch("setUser", null);
      this.$store.dispatch("setIsAuthenticated", 0);
      this.$router.push("/");
    }
    if (this.$store.state.user?.user?.usertypeID == 3) {
      this.activeRole = this.$store.state.activeRole;
      this.getRoles();
    }
  },

  data: () => ({
    activeTab: "password",
    // roles tab
    roles: [],
    rolesLoading: false,
    activeRole: null,
    // password tab
    cur_pass: null,
    new_pass: null,
    confirm_pass: null,
    show_cur_pass: false,
    show_new_pass: false,
    show_confirm_pass: false,
    btnLoading: false,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "System Message",
      message: "",
      top: 10,
    },
  }),

  computed: {
    isFirstTimeLogin() {
      return this.$store.state.user?.user?.isFirstTimeLogin == 1;
    },
    isCoordinator() {
      return this.$store.state.user?.user?.usertypeID == 3;
    },
  },

  methods: {
    getRoles() {
      this.rolesLoading = true;
      this.axiosCall("/user-role/getMyRoles", "GET").then((res) => {
        this.roles = res.data;
        this.rolesLoading = false;
      });
    },
    selectRole(id) {
      this.activeRole = id;
      this.$store.dispatch("setActiveRole", id);
    },
    palette(idx) {
      return ROLE_PALETTE[idx % ROLE_PALETTE.length];
    },
    async submit() {
      let validate = await this.$refs.ChangePassRef.validate();
      this.btnLoading = true;
      if (validate.valid) {
        let data = {
          new_password: this.new_pass,
          old_password: this.cur_pass,
        };
        this.axiosCall("/auth/change-pass", "Patch", data).then((res) => {
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.message = res.data.msg;
            this.fadeAwayMessage.header = "System Message";

            if (this.isFirstTimeLogin) {
              setTimeout(() => {
                this.btnLoading = false;
                this.logout();
              }, 3000);
            } else {
              setTimeout(() => {
                this.btnLoading = false;
                this.cur_pass = null;
                this.new_pass = null;
                this.confirm_pass = null;
                this.$refs.ChangePassRef.reset();
              }, 2000);
            }
          } else {
            this.btnLoading = false;
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.message = res.data.msg;
            this.fadeAwayMessage.header = "System Message";
          }
        });
      } else {
        this.btnLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════
   FIRST-TIME LOGIN — fixed overlay
══════════════════════════════ */
.change-pass-page {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  overflow: hidden;
}

.cp-layout {
  display: flex;
  width: 100%;
  height: 100%;
}

.left-panel {
  width: 50%;
  height: 100%;
  background-image: url("../../assets/img/Container.png");
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 40px;
}

.lp-logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lp-logo-box {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.lp-logo-box img {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.lp-logo-text {
  color: white;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.3px;
}

.lp-hero {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-bottom: 32px;
}

.lp-tagline {
  color: white;
  font-size: 46px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -1px;
  width: 58%;
  margin-bottom: 18px;
}

.lp-tagline-green {
  color: #5ee9b5;
  display: block;
}

.lp-caption {
  color: #d0fae5e5;
  font-size: 16px;
  line-height: 1.7;
  width: 54%;
  letter-spacing: -0.2px;
}

.lp-bottom {
  color: #a4f4cf99;
}

.lp-features {
  margin-bottom: 28px;
}

.lp-feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 16px;
}

.lp-divider {
  background-color: rgba(164, 244, 207, 0.25);
  height: 1px;
  border: 0;
  margin-bottom: 12px;
}

.lp-footer {
  font-size: 13px;
  color: #a4f4cf80;
}

.right-panel {
  flex: 1;
  height: 100%;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.cp-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 420px;
  padding: 40px 36px;
  text-align: center;
}

.card-logo-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.lc-title {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.3;
}

.lc-subtitle {
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
}

/* ══════════════════════════════
   SETTINGS PAGE
══════════════════════════════ */
.settings-page {
  padding: 32px 36px;
  max-width: 860px;
}

.settings-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
  margin: 0 0 4px;
}

.settings-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
}

.settings-tab-bar {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 28px;
}

.settings-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}

.settings-tab:hover {
  color: #111827;
}

.settings-tab--active {
  color: #10b981;
  border-bottom-color: #10b981;
}

.settings-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Change Password card */
.cp-settings-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.cp-settings-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
}

.cp-settings-icon-wrap {
  width: 40px;
  height: 40px;
  background: #ecfdf5;
  border: 1px solid #d1fae5;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cp-settings-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}

.cp-settings-desc {
  font-size: 12.5px;
  color: #9ca3af;
}

.pass-requirements {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
}

.pass-req-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px;
}

.pass-req-list {
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pass-req-list li {
  font-size: 13px;
  color: #6b7280;
}

/* Shared */
.field-group {
  text-align: left;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.submit-btn {
  font-weight: 600;
  letter-spacing: 0.2px;
}

/* ── Role cards (Switch Role tab) ── */
.role-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  user-select: none;
}
.role-card--idle {
  background: #ffffff;
  border-color: #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}
.role-card--idle:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  border-color: #a7f3d0;
}
.role-card--active {
  background: #f0fdf4;
  border-color: #10b981;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.15);
}
.role-icon-box {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.role-name {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
}
.role-hint {
  font-size: 12px;
  color: #94a3b8;
}
.active-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: #10b981;
  border-radius: 0 0 10px 10px;
}

/* Responsive — first-time login only */
@media (max-width: 959px) {
  .left-panel { display: none; }
  .right-panel { width: 100%; }
  .cp-card { padding: 32px 24px; }
}

@media (max-width: 479px) {
  .lc-title { font-size: 22px; }
  .cp-card { padding: 28px 20px; border-radius: 12px !important; }
}
</style>
